import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Caption1, DisplayCurrency, DisplayHours, InfoIcon, Tooltip } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { getJobTypeById } from "~/store/selectors/selector.jobTypes";
import { ROW_CLASSES_ITEM, ROW_CLASSES_SHARED } from "./utils";

export const CostSummaryJobTypeCostItem = ({
  jobTypeId,
  hours,
  rate,
  currencyCode,
  defaultCurrency,
  internalCost,
  thousandSeparator,
  decimalSeparator,
  decimalSpaces,
  total,
  withoutInternalRate,
}) => {
  const { t } = useTranslation();
  const jobType = useSelector((state) => getJobTypeById(state, jobTypeId));

  return (
    <div className={classNames(ROW_CLASSES_ITEM, ROW_CLASSES_SHARED)}>
      <Caption1
        color="secondary"
        weight="bold"
      >
        <DisplayHours
          value={hours}
          trimZeroMinutes={false}
          format="short"
          as="span"
          variant="Caption 1"
          color="secondary"
          weight="bold"
          decimalSeparator={decimalSeparator}
          decimalSpaces={decimalSpaces}
          thousandSeparator={thousandSeparator}
        />{" "}
        h
      </Caption1>
      <Caption1
        color="tertiary"
        className="tw-min-w-0 tw-flex tw-items-center tw-gap-1"
      >
        <div className="tw-truncate">{jobType.name}</div>
        <div className="tw-shrink-0">
          @
          <DisplayCurrency
            variant="Caption 1"
            color="tertiary"
            currencyCode={currencyCode}
            currencyPosition="right"
            decimalSeparator={decimalSeparator}
            decimalSpaces={decimalSpaces}
            thousandSeparator={thousandSeparator}
            format="long"
            value={rate}
            trimDecimals={false}
            as="span"
          />
          /h
        </div>
      </Caption1>
      <div className="tw-flex tw-gap-1.5 tw-text-right tw-items-center tw-justify-end">
        {withoutInternalRate ? (
          <Tooltip title={t("Internal rates missing")}>
            <InfoIcon
              fill="var(--red-alert)"
              className="tw-w-[18px] tw-h-[18px]"
            />
          </Tooltip>
        ) : null}
        <DisplayCurrency
          variant="Caption 1"
          weight="bold"
          align="right"
          as="span"
          color="secondary"
          currencyCode={defaultCurrency}
          currencyPosition="right"
          decimalSeparator={decimalSeparator}
          decimalSpaces={decimalSpaces}
          format="short"
          thousandSeparator={thousandSeparator}
          value={internalCost}
          trimDecimals={false}
        />
      </div>
      <DisplayCurrency
        variant="Caption 1"
        weight="bold"
        align="right"
        color="secondary"
        currencyCode={currencyCode}
        currencyPosition="right"
        decimalSeparator={decimalSeparator}
        decimalSpaces={decimalSpaces}
        format="short"
        thousandSeparator={thousandSeparator}
        value={total}
        trimDecimals={false}
      />
    </div>
  );
};

CostSummaryJobTypeCostItem.propTypes = {
  jobTypeId: PropTypes.number.isRequired,
  hours: PropTypes.number.isRequired,
  rate: PropTypes.number.isRequired,
  currencyCode: PropTypes.string.isRequired,
  defaultCurrency: PropTypes.string.isRequired,
  internalCost: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  thousandSeparator: PropTypes.string,
  decimalSeparator: PropTypes.string,
  decimalSpaces: PropTypes.number,
  withoutInternalRate: PropTypes.bool,
};

CostSummaryJobTypeCostItem.defaultProps = {
  decimalSeparator: ".",
  thousandSeparator: ",",
  decimalSpaces: 2,
};
